/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { ReactElement, ReactNode } from "react";
import { Provider, ReactReduxContext } from "react-redux";
import type { AppProps } from "next/app";
import type { NextPage } from "next";

import { SSRConfig, appWithTranslation } from "next-i18next";

// Must stay above every component import so the global stylesheet is emitted
// before any CSS module and can be overridden by it.
import "../styles/globals.scss";
import "../i18n";

import { IConfigurationModel, ReduxStoreConfigurator } from "@nf/common";

import dynamic from "next/dynamic";

import Root from "./root";
import Layout from "layouts/Layout";
import RouteProtector from "../components/RouteProtector";
import DefaultSEO from "../components/SEO/DefaultSEO";

const CookieConsent = dynamic(() => import("../components/CookieConsent"), {
  ssr: false,
});

import i18n from "../i18n";

import { IsLoadingContextProvider } from "helpers/setIsLoadingContext";
import { PageOverlay } from "../components/PageOverlay/PageOverlay";
import { DixaScript } from "../components";

const reduxStoreConfigurator = new ReduxStoreConfigurator();
const appStore = reduxStoreConfigurator.initStore();

interface CustomPageProps {
  configuration?: IConfigurationModel;
}

type NextPageWithLayout = NextPage<CustomPageProps> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps<CustomPageProps> & {
  Component: NextPageWithLayout;
};

type AppPropsWithTranslations = AppProps<CustomPageProps> & {
  pageProps: SSRConfig;
};

console.log(
  `%cVERSION: %c${process.env.NEXT_PUBLIC_VERSION}`,
  "color: green;font-weight: bold;",
  "color: red;font-weight: bold;"
);

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const configuration = pageProps.configuration;

  // Use the layout defined at the page level, if available
  const getLayout =
    Component.getLayout ??
    ((page) => <Layout config={configuration}>{page}</Layout>);

  return (
    <>
      <DefaultSEO />
      {configuration?.Scripts?.DixaMessengerToken && (
        <DixaScript
          messengerToken={configuration?.Scripts?.DixaMessengerToken}
        />
      )}
      <Provider store={appStore} context={ReactReduxContext}>
        <CookieConsent />
        <IsLoadingContextProvider>
          {getLayout(
            <Root config={configuration}>
              <RouteProtector>
                <PageOverlay>
                  <Component {...pageProps} />
                </PageOverlay>
              </RouteProtector>
            </Root>
          )}
        </IsLoadingContextProvider>
      </Provider>
    </>
  );
}

export default appWithTranslation<AppPropsWithTranslations>(MyApp, i18n);
